import axiosClient from "./axiosClient";
import { ScoredTeam, TrainingProposal, TrainingSession, CalendarEvent, Player, ParticipantTeam, ParticipantTeamDetail, ParticipantPlayerDetail, SaveParticipantPlayerRequest, TournamentPhase, Season, Tournament, RosterEntry, RosterEntryRequest, GalleryItem, NextMatchInfo, MatchSummary, HeadToHead, SeasonComparison, TrainingSummary } from "./types";
import type { LiveGameEvent } from "../components/LiveMatch/liveMatchTypes.ts";

export const playerService = {
  getPlayers: async (): Promise<Player[]> => {
    const res = await axiosClient.get("/players");
    return res.data;
  },

  updatePlayerCertificate: async (playerId: number, newDate: string | Date) => {
    let dateStr: string;

    if (newDate instanceof Date) {
      dateStr = newDate.toISOString().split("T")[0];
    } else if (typeof newDate === "string") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
        const parsed = new Date(newDate);
        if (isNaN(parsed.getTime())) {
          throw new Error("Formato data non valido. Usa yyyy-MM-dd oppure un oggetto Date.");
        }
        dateStr = parsed.toISOString().split("T")[0];
      } else {
        dateStr = newDate;
      }
    } else {
      throw new Error("Unsupported date type");
    }

    await axiosClient.put(`/players/${playerId}/certificate`, {
      suitabilityCertificate: dateStr,
    });
  },
};

export const formationService = {
  getBestFormations: async (convocati: number[], p0: { type: "all" | "topN" | "topNEveryone"; topN: number }) => {
    const type = p0.type;
    const teams = type === "all" ? 0 : p0.topN;
    const res = await axiosClient.post("/optimizeById", { convocati, type, teams });
    return res.data;
  },

  getTrainingTeams: async (convocati: number[], setters?: number[]): Promise<TrainingProposal> => {
    const res = await axiosClient.post("/training", { convocati, setters });
    return res.data;
  },

  /** Rotazione panchina: composizione proposta per il set successivo */
  getNextTrainingSet: async (req: {
    teamA: { playerId: number; role: string }[];
    teamB: { playerId: number; role: string }[];
    bench: number[];
    setters: number[];
    playedSets: Record<number, number>;
  }): Promise<TrainingProposal> => {
    const res = await axiosClient.post("/training/next-set", req);
    return res.data;
  },

  evaluateTeams: async (teams: ScoredTeam[]) => {
    const response = await axiosClient.post("/evaluate", teams);
    return response.data;
  },
};

export const trainingService = {
  saveTrainingSession: async (session: TrainingSession) => {
    const normalizedSession = {
      ...session,
      sets: session.sets.map(set => ({
        ...set,
        teamA: set.teamA.map(p => p.id),
        teamB: set.teamB.map(p => p.id),
        bench: (set.bench ?? []).map(p => p.id),
      })),
    };
    const response = await axiosClient.post("/saveTraining", normalizedSession);
    return response.data;
  },

  getTrainingsBetween: async (startDate: any, endDate: any) => {
    const res = await axiosClient.get(`/getTrainingsBetween?from=${startDate}&to=${endDate}`);
    return res.data;
  },

  deleteTraining: async (id?: number) => {
    const res = await axiosClient.delete(`/deleteTraining/${id}`);
    return res.data;
  },
};

export const trainingSetService = {
  removePlayer: async (setId: number, team: "teamA" | "teamB", playerId: number) => {
    const res = await axiosClient.delete(`/training/sets/${setId}/player`, {
      data: { team, playerId },
    });
    return res.data;
  },

  movePlayer: async (setId: number, fromTeam: "teamA" | "teamB", toTeam: "teamA" | "teamB", playerId: number) => {
    const res = await axiosClient.post(`/training/sets/${setId}/move`, {
      fromTeam,
      toTeam,
      playerId,
    });
    return res.data;
  },

  updateScore: async (setId: number, scoreA: number, scoreB: number) => {
    const res = await axiosClient.put(`/training/sets/${setId}/score`, {
      scoreA,
      scoreB,
    });
    return res.data;
  },
};

export const matchService = {
  getMatchesBetween: async (startDate: any, endDate: any) => {
    const res = await axiosClient.get(`/matches/getMatchesBetween?from=${startDate}&to=${endDate}`);
    return res.data;
  },

  deleteMatch: async (id?: number) => {
    const res = await axiosClient.delete(`/matches/deleteMatch/${id}`);
    return res.data;
  },
};

export const calendarService = {
  /** Precedenti con l'avversario della partita (null se l'avversario non è una squadra partecipante) */
  getHeadToHead: async (eventId: number): Promise<HeadToHead | null> => {
    try {
      const res = await axiosClient.get(`/calendar/events/${eventId}/head-to-head`);
      return res.data;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },

  createEvent: async (event: Partial<CalendarEvent>) => {
    const res = await axiosClient.post(`/calendar`, event);
    return res.data;
  },

  updateEvent: async (eventId: number, event: Partial<CalendarEvent>) => {
    const res = await axiosClient.put(`/calendar/${eventId}`, event);
    return res.data;
  },

  deleteEvent: async (eventId: number) => {
    const res = await axiosClient.delete(`/calendar/${eventId}`);
    return res.data;
  },

  updateEventDate: async (eventId: number, date: string) => {
    const res = await axiosClient.put(`/calendar/${eventId}/date`, { date });
    return res.data;
  },

  getTournamentPhases: async (tournamentId: number): Promise<TournamentPhase[]> => {
    const res = await axiosClient.get(`/calendar/${tournamentId}/phases`);
    return res.data;
  },

  /** Prossima partita della stagione corrente (qualsiasi torneo) */
  getNextMatch: async (): Promise<NextMatchInfo | null> => {
    try {
      const res = await axiosClient.get(`/calendar/next-match`);
      return res.data;
    } catch {
      return null;
    }
  },
};

export const teamPatternService = {
  getBestGroup: async (params?: { month?: number; year?: number; source?: string }) => {
    const res = await axiosClient.get(`/bestGroup`, { params });
    return res.data;
  },
  getMonthlyTrend: async (source: string) => {
    const res = await axiosClient.get(`/bestGroup/monthly`, { params: { source } });
    return res.data;
  },
};

export const participantService = {
  getSeasonComparison: async (compareTo?: number | null): Promise<SeasonComparison> => {
    const res = await axiosClient.get("/participants/season-comparison", { params: compareTo ? { compareTo } : {} });
    return res.data;
  },

  getTeamHeadToHead: async (teamId: number): Promise<HeadToHead> => {
    const res = await axiosClient.get(`/participants/team/${teamId}/head-to-head`);
    return res.data;
  },

  getTeams: async (): Promise<ParticipantTeam[]> => {
    const res = await axiosClient.get("/participants");
    return res.data;
  },

  getTeamsWithPlayers: async (): Promise<ParticipantTeamDetail[]> => {
    const res = await axiosClient.get("/participants/getAll");
    return res.data;
  },

  updateTeam: async (teamId: number, data: object): Promise<ParticipantTeamDetail> => {
    const res = await axiosClient.put(`/participants/team/${teamId}`, data);
    return res.data;
  },

  addPlayer: async (teamId: number, data: SaveParticipantPlayerRequest): Promise<ParticipantPlayerDetail> => {
    const res = await axiosClient.post(`/participants/team/${teamId}/players`, data);
    return res.data;
  },

  updatePlayer: async (playerId: number, data: SaveParticipantPlayerRequest): Promise<ParticipantPlayerDetail> => {
    const res = await axiosClient.put(`/participants/players/${playerId}`, data);
    return res.data;
  },

  deletePlayer: async (playerId: number): Promise<void> => {
    await axiosClient.delete(`/participants/players/${playerId}`);
  },
};

export interface LiveMatchSessionDTO {
  id: number;
  matchEventId: number;
  strategy: string | null;
  servingTeam: string | null;
  status: string;
  startedAt: string;
  completedAt: string | null;
  eventLog: string | null;
}

export const liveMatchService = {
  startOrResume: async (eventId: number, strategy: string, servingTeam: string): Promise<LiveMatchSessionDTO> => {
    const res = await axiosClient.post("/live-match/start-or-resume", { eventId, strategy, servingTeam });
    return res.data;
  },

  getByEvent: async (eventId: number): Promise<LiveMatchSessionDTO | null> => {
    try {
      const res = await axiosClient.get(`/live-match/by-event/${eventId}`);
      return res.data;
    } catch {
      return null;
    }
  },

  updateLog: async (sessionId: number, eventLog: string, callupDataJson?: string): Promise<void> => {
    await axiosClient.put(`/live-match/${sessionId}/log`, { eventLog, callupDataJson });
  },

  complete: async (sessionId: number): Promise<void> => {
    await axiosClient.put(`/live-match/${sessionId}/complete`);
  },

  getEventLog: async (eventId: number): Promise<LiveGameEvent[] | null> => {
    try {
      const res = await axiosClient.get(`/live-match/by-event/${eventId}/event-log`);
      return res.data;
    } catch {
      return null;
    }
  },

  getCallupData: async (eventId: number): Promise<any | null> => {
    try {
      const res = await axiosClient.get(`/live-match/by-event/${eventId}/callup`);
      return res.data;
    } catch {
      return null;
    }
  },

  getActive: async (): Promise<LiveMatchSessionDTO | null> => {
    try {
      const res = await axiosClient.get("/live-match/active");
      return res.data;
    } catch {
      return null;
    }
  },
};

export const configService = {
  getTeamName: async (): Promise<string> => {
    const res = await axiosClient.get("/config/team-name");
    return res.data.teamName;
  },
};

export const seasonService = {
  getSeasons: async (): Promise<Season[]> => {
    const res = await axiosClient.get("/seasons");
    return res.data;
  },
  /** Tornei della stagione selezionata (header X-Season-Id) */
  getTournaments: async (): Promise<Tournament[]> => {
    const res = await axiosClient.get("/tournaments");
    return res.data;
  },
};

export const rosterService = {
  getRoster: async (seasonId: number): Promise<RosterEntry[]> => {
    const res = await axiosClient.get(`/seasons/${seasonId}/roster`);
    return res.data;
  },
  getImportCandidates: async (seasonId: number, fromSeasonId: number): Promise<RosterEntry[]> => {
    const res = await axiosClient.get(`/seasons/${seasonId}/roster/import-candidates`, { params: { fromSeasonId } });
    return res.data;
  },
  add: async (seasonId: number, entry: RosterEntryRequest): Promise<RosterEntry> => {
    const res = await axiosClient.post(`/seasons/${seasonId}/roster`, entry);
    return res.data;
  },
  update: async (seasonId: number, playerId: number, entry: RosterEntryRequest): Promise<RosterEntry> => {
    const res = await axiosClient.put(`/seasons/${seasonId}/roster/${playerId}`, entry);
    return res.data;
  },
  remove: async (seasonId: number, playerId: number): Promise<void> => {
    await axiosClient.delete(`/seasons/${seasonId}/roster/${playerId}`);
  },
  importFrom: async (seasonId: number, fromSeasonId: number, playerIds: number[]): Promise<RosterEntry[]> => {
    const res = await axiosClient.post(`/seasons/${seasonId}/roster/import`, { fromSeasonId, playerIds });
    return res.data;
  },
};

export const galleryService = {
  /** Partite con foto di squadra, tutte le stagioni, dalla più recente */
  getGallery: async (): Promise<GalleryItem[]> => {
    const res = await axiosClient.get("/matches/gallery");
    return res.data;
  },
  teamPhotoUrl: (matchId: number) => `${process.env.REACT_APP_BASE_URL}matches/${matchId}/images/team_photo.jpg`,
};

export const homeService = {
  /** Riepilogo partite della stagione selezionata (vittorie, set, ultime partite) */
  getMatchSummary: async (): Promise<MatchSummary> => {
    const res = await axiosClient.get("/dashboard/match-summary");
    return res.data;
  },
  /** Allenamenti della stagione selezionata e giocatori più presenti */
  getTrainingSummary: async (): Promise<TrainingSummary> => {
    const res = await axiosClient.get("/dashboard/training-summary");
    return res.data;
  },
};

export const archiveService = {
  /** Carica una foto nell'archivio (solo ADMIN): data precisa, oppure anno + stagione */
  upload: async (file: File, opts: { date?: string; year?: number; season?: string }) => {
    const form = new FormData();
    form.append("file", file);
    if (opts.date) form.append("date", opts.date);
    if (opts.year) form.append("year", String(opts.year));
    if (opts.season) form.append("season", opts.season);
    const res = await axiosClient.post("/archive/photos", form, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data as { id: string; duplicate: boolean };
  },
  remove: async (id: string) => {
    await axiosClient.delete(`/archive/photos/${id}`);
  },
};
